import type { Block } from '@worldathletics/types';
import type { DocumentEntity, DocumentsState } from './slices/documentDataSlice';

export const normalizeDocumentData = (items: Block[]): DocumentsState => {
  const state: DocumentsState = {
    byId: {},
    rootIds: [],
    childrenById: {},
    parentById: {},
    isLoaded: true,
    isDirty: false,
    sourceNodeKey: null,
    pendingOps: [],
    nextPendingSequence: 0,
    structureRevision: 0,
    namingRevision: 0,
    contentRevision: 0,
    serverBlockIds: [],
  };

  const visit = (nodes: Block[], parentId: string | null) => {
    const sorted = [...nodes].sort((a, b) => a.order - b.order);
    const ids: string[] = [];

    for (const node of sorted) {
      const isContainer = node.type === 'container';
      const { blocks, ...entity } = isContainer
        ? node
        : { ...node, blocks: undefined as Block[] | undefined };

      state.byId[node.id] = entity as DocumentEntity;
      state.parentById[node.id] = parentId;
      ids.push(node.id);

      if (isContainer && blocks?.length) {
        visit(blocks, node.id);
      } else {
        state.childrenById[node.id] = [];
      }
    }

    if (parentId === null) {
      state.rootIds = ids;
    } else {
      state.childrenById[parentId] = ids;
    }
  };

  visit(items, null);

  return state;
};

export const denormalizeDocumentData = (store: DocumentsState): Block[] => {
  function buildTree(ids: string[]): Block[] {
    return ids.map(id => {
      const entity = store.byId[id];

      if (!entity) {
        throw new Error(`Missing entity for id "${id}"`);
      }

      const childIds = store.childrenById[id] ?? [];

      if (entity.type === 'container') {
        return {
          ...entity,
          blocks: buildTree(childIds),
        } as Block;
      }

      // Leaves (text / table / image) don't carry a `blocks` field.
      return entity as Block;
    });
  }

  return buildTree(store.rootIds);
};

export const collectDescendantIds = (
  state: DocumentsState,
  id: string,
): string[] => {
  const result: string[] = [];

  const walk = (nodeId: string) => {
    result.push(nodeId);
    const children = state.childrenById[nodeId] ?? [];
    children.forEach(walk);
  };

  walk(id);
  return result;
};

export const reindexChildren = (
  state: DocumentsState,
  parentId: string | null,
) => {
  const ids =
    parentId === null ? state.rootIds : (state.childrenById[parentId] ?? []);

  ids.forEach((id, index) => {
    if (state.byId[id]) {
      state.byId[id].order = index;
    }
  });
};

export const addNodeRecursive = (
  state: DocumentsState,
  node: Block,
  parentId: string | null,
) => {
  const isContainer = node.type === 'container';
  const { blocks, ...entity } = isContainer
    ? node
    : { ...node, blocks: undefined as Block[] | undefined };

  state.byId[node.id] = entity as DocumentEntity;
  state.parentById[node.id] = parentId;
  state.childrenById[node.id] = [];

  if (!isContainer) {
    return;
  }

  if (blocks?.length) {
    const sorted = [...blocks].sort((a, b) => a.order - b.order);

    state.childrenById[node.id] = sorted.map(c => c.id);

    sorted.forEach((child, index) => {
      child.order = index;
      addNodeRecursive(state, child, node.id);
    });
  }
};

export const isDescendant = (
  state: DocumentsState,
  ancestorId: string,
  targetId: string,
): boolean => {
  let current = state.parentById[targetId];

  while (current) {
    if (current === ancestorId) return true;
    current = state.parentById[current];
  }

  return false;
};

export const getSiblingCount = (
  state: DocumentsState,
  parentId: string | null,
): number => {
  if (parentId === null) {
    return state.rootIds.length;
  }

  return state.childrenById[parentId]?.length ?? 0;
};

export const getSiblingIndex = (
  state: DocumentsState,
  id: string,
): number => {
  if (!(id in state.parentById)) {
    return -1;
  }
  const parentId = state.parentById[id];
  const siblings =
    parentId === null ? state.rootIds : (state.childrenById[parentId] ?? []);
  return siblings.indexOf(id);
};
